import {useEffect, useRef} from 'react';
import * as Sentry from '@sentry/react';
import {usePostHog} from 'posthog-js/react';
import {useUserInfo} from './useUserInfo';
import {analytics} from '@/services/analytics';

export function usePostHogIdentify() {
  const posthog = usePostHog();
  const userInfo = useUserInfo();
  const lastIdentifiedUserRef = useRef<string | null>(null);

  // Set PostHog instance on analytics service when available
  useEffect(() => {
    if (posthog) {
      analytics.setPostHog(posthog);
    }
  }, [posthog]);

  useEffect(() => {
    if (!posthog) return;

    if (userInfo.id && userInfo.orgId) {
      // Only identify if this is a different user or first time
      if (lastIdentifiedUserRef.current !== userInfo.id) {
        const properties = {
          email: userInfo.email,
          fullName: userInfo.fullName,
          role: userInfo.role,
          orgId: userInfo.orgId,
          organizationName: userInfo.organizationName,
        };

        // Remove null values
        const cleanedProperties = Object.fromEntries(
          Object.entries(properties).filter(([, value]) => value !== null)
        );

        posthog.identify(`admin:${userInfo.id}`, cleanedProperties);
        Sentry.setUser({
          id: userInfo.id,
          email: userInfo.email ?? undefined,
          username: userInfo.fullName ?? undefined,
        });
        Sentry.setTag('orgId', userInfo.orgId);
        lastIdentifiedUserRef.current = userInfo.id;
        console.log('[PostHog] User identified:', { userId: userInfo.id, properties: cleanedProperties });

        // Register super properties to include with every event
        analytics.registerSuperProperties({
          orgId: userInfo.orgId,
          org_id: userInfo.orgId,
          userRole: userInfo.role,
          role: userInfo.role,
        });
        posthog.group('organization', userInfo.orgId);
      }
    } else if (!userInfo.id && lastIdentifiedUserRef.current !== null) {
      // User logged out - reset session only once
      analytics.reset();
      Sentry.setUser(null);
      lastIdentifiedUserRef.current = null;
      console.log('[PostHog] User session reset');
    }
  }, [posthog, userInfo.id, userInfo.orgId, userInfo.role]);

  return posthog;
}